/**
 * Conversion PDP — bundle builder
 *
 * Lets a customer fill a bundle with different models, colours and sizes.
 * Each pair is its own row and becomes its own cart line, so the theme's
 * single-variant product form cannot be used for the add — this posts an
 * items[] array to Shopify's cart API instead.
 *
 * The pair count is the source of truth: changing it selects the matching
 * bundle tier, and selecting a tier sets the count. That keeps the price the
 * customer sees and the number of pairs they are buying in step.
 */
/**
 * Section ids the cart renders into, so the add request can return their HTML
 * in the same round trip.
 *
 * Without this the cart components have to fetch the section separately after
 * the drawer has already opened, which shows the customer an empty cart for
 * the length of that request. The theme's own product form does the same thing.
 */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((node) => node.dataset.sectionId)
    .filter(Boolean);
}

/**
 * Put the rendered cart HTML into the page before anyone is told the cart
 * changed.
 *
 * Order is the whole point. The drawer opens the moment the event fires, while
 * the cart list morphs a tick later — so the customer sees the drawer's stale
 * contents first. Worse, morphing away from the empty state triggers a view
 * transition, which then plays as a visible sweep across an already-open
 * drawer. Morphing first means the drawer opens on its final contents and that
 * transition never has an empty state to animate from.
 */
async function applyCartSections(sections) {
  if (!sections) return;

  try {
    const { morphSection } = await import('@theme/section-renderer');

    await Promise.all(
      Object.entries(sections).map(([id, html]) => {
        const target = document.querySelector(`cart-items-component[data-section-id="${id}"]`);
        if (!html || !target) return null;

        // The drawer hydrates; a full cart page replaces outright. The
        // stylesheet has to come along, since the filled state adds summary
        // markup the empty state never rendered.
        return morphSection(id, html, {
          mode: target.hasAttribute('data-drawer') ? 'hydration' : 'full',
          injectStylesheet: true,
        });
      })
    );
  } catch (error) {
    // Not fatal: the cart components refetch the section themselves.
    console.warn('[cart] could not pre-render cart sections', error);
  }
}

class ConversionBundle extends HTMLElement {
  connectedCallback() {
    // One JSON tag per product, so a product the theme could not describe is
    // simply absent rather than corrupting a shared array.
    this.products = [...this.querySelectorAll('[data-cb-product-data]')]
      .map((node) => this.#parse(node))
      .filter(Boolean);
    if (!this.products.length) return;

    this.rowsEl = this.querySelector('[data-cb-rows]');
    this.addBtn = this.querySelector('[data-cb-add]');
    this.benefitEl = this.querySelector('[data-cb-benefit]');
    this.disclosure = this.querySelector('[data-cb-disclosure]');
    this.summaryEl = this.querySelector('[data-cb-summary]');
    this.form = this.closest('conversion-pdp')?.querySelector('form[data-type="add-to-cart-form"]');

    // Tier radios live outside this element, next to the bundle cards.
    this.tiers = [...(this.closest('conversion-pdp')?.querySelectorAll('[data-cpdp-tier]') ?? [])];

    // Every pair count has to map onto a tier, otherwise the customer could
    // build a bundle the price above it does not describe. The tiers, not the
    // setting, decide how far the builder goes.
    const quantities = this.tiers.map((tier) => Number(tier.value)).filter((n) => n > 0);
    this.minPairs = quantities.length ? Math.min(...quantities) : 1;
    this.maxPairs = quantities.length
      ? Math.max(...quantities)
      : Number(this.dataset.maxPairs) || 6;

    this.pdp = this.closest('conversion-pdp');
    this.colourPosition = Number(this.dataset.colourPosition) || 0;
    this.sizePosition = Number(this.dataset.sizePosition) || 0;

    this.addBtn?.addEventListener('click', () => this.#addRow());
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);

    // The swatches and size pills above the bundle drive the rows that are
    // still set to the product being viewed.
    this.pdp?.addEventListener('change', this.#onOptionChange);

    for (const tier of this.tiers) {
      tier.addEventListener('change', () => {
        if (!tier.checked) return;
        this.#setCount(Number(tier.value) || 1);
        this.#reveal();
      });
    }

    // Clicking the tier that is already selected fires no `change` event, and
    // two pairs is selected from the start — so opening the panel has to hang
    // off the click itself, or the most common path would never open it.
    this.pdp?.addEventListener('click', this.#onTierClick);

    // The section withholds `on:submit` from <product-form-component> while the
    // builder is on, so nothing else is competing for this event.
    this.form?.addEventListener('submit', this.#onSubmit);

    const initial = this.tiers.find((t) => t.checked);
    this.#setCount(Number(initial?.value) || 1);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.pdp?.removeEventListener('change', this.#onOptionChange);
    this.pdp?.removeEventListener('click', this.#onTierClick);
  }

  /** Open the panel, but only once there are pairs worth mixing. */
  #reveal() {
    if (this.disclosure && this.rows.length > 1) this.disclosure.open = true;
  }

  #onTierClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.cpdp__tier')) return;
    // The count is set by the change handler, or is already correct when the
    // tier was selected — either way this only has to handle the opening.
    requestAnimationFrame(() => this.#reveal());
  };

  #parse(node) {
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[conversion-bundle] unreadable product data', error);
      return null;
    }
  }

  get rows() {
    return [...this.querySelectorAll('[data-cb-row]')];
  }

  /* ------------------------------------------------------------ rows --- */

  #setCount(n) {
    const target = Math.max(this.minPairs, Math.min(n, this.maxPairs));
    while (this.rows.length > target) this.rows[this.rows.length - 1].remove();
    while (this.rows.length < target) this.#appendRow();
    this.#refresh();
  }

  #addRow() {
    if (this.rows.length >= this.maxPairs) return;
    this.#appendRow();
    this.#syncTierToCount();
    this.#refresh();
  }

  #appendRow() {
    const tpl = this.querySelector('template[data-cb-template]');
    if (!tpl || !this.rowsEl) return;

    const row = /** @type {HTMLElement} */ (tpl.content.firstElementChild.cloneNode(true));
    const productSel = row.querySelector('[data-cb-product]');

    for (const product of this.products) {
      productSel?.append(new Option(product.title, product.id));
    }

    const preferred = this.dataset.defaultProduct;
    if (productSel && this.products.some((p) => String(p.id) === preferred)) {
      productSel.value = preferred;
    }

    this.rowsEl.append(row);
    this.#fillRow(row);
    // A new pair opens on whatever is selected above rather than on the first
    // colour in the list.
    this.#syncRow(row);
  }

  /** Populate colour and size for a row from its chosen product. */
  #fillRow(row, keep = false) {
    const product = this.#productOf(row);
    if (!product) return;

    const colourSel = row.querySelector('[data-cb-colour]');
    const sizeSel = row.querySelector('[data-cb-size]');
    const prevColour = keep ? colourSel.value : null;

    colourSel.replaceChildren();
    for (const c of product.colours) colourSel.append(new Option(c, c));
    if (prevColour && product.colours.includes(prevColour)) colourSel.value = prevColour;

    const prevSize = keep ? sizeSel.value : null;
    sizeSel.replaceChildren();
    for (const s of product.sizes) {
      const variant = this.#variant(product, colourSel.value, s);
      const opt = new Option(variant?.available ? s : `${s} — sold out`, s);
      opt.disabled = !variant?.available;
      sizeSel.append(opt);
    }
    if (prevSize && [...sizeSel.options].some((o) => o.value === prevSize && !o.disabled)) {
      sizeSel.value = prevSize;
    } else {
      const firstAvailable = [...sizeSel.options].find((o) => !o.disabled);
      if (firstAvailable) sizeSel.value = firstAvailable.value;
    }
  }

  #productOf(row) {
    const id = row.querySelector('[data-cb-product]')?.value;
    return this.products.find((p) => String(p.id) === String(id)) ?? this.products[0];
  }

  #variant(product, colour, size) {
    return product.variants.find((v) => v.colour === colour && v.size === size);
  }

  /* -------------------------------------------------------- reactions --- */

  #onChange = (event) => {
    const row = event.target.closest('[data-cb-row]');
    if (!row) return;

    // Changing the model replaces the colour and size lists entirely;
    // changing the colour only re-tests which sizes are in stock.
    if (event.target.matches('[data-cb-product]')) {
      this.#fillRow(row);
      // A row put back on this product may follow the swatches again — the
      // colour and size it had belonged to a different shoe.
      delete row.dataset.touched;
      this.#syncRow(row);
    } else if (event.target.matches('[data-cb-colour]')) {
      this.#fillRow(row, true);
      row.dataset.touched = 'true';
    } else if (event.target.matches('[data-cb-size]')) {
      row.dataset.touched = 'true';
    }

    this.#refresh();
  };

  /**
   * Mirror a selection made in the swatches or size pills above into the rows.
   *
   * Only rows still set to the product being viewed are affected — a pair the
   * customer switched to another model has nothing to do with this page's
   * swatches. A row the customer has set by hand is left alone too, otherwise
   * picking two colours of the same shoe would be impossible.
   */
  #onOptionChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (this.contains(input) || !input.matches('[data-cpdp-option]')) return;

    const position = Number(input.dataset.optionPosition);
    if (position !== this.colourPosition && position !== this.sizePosition) return;

    for (const row of this.rows) this.#syncRow(row);
    this.#refresh();
  };

  /** The colour and size currently checked in the pickers above. */
  #currentSelection() {
    const selection = {};
    for (const input of this.pdp?.querySelectorAll('[data-cpdp-option]') ?? []) {
      if (!input.checked) continue;
      const position = Number(input.dataset.optionPosition);
      if (position === this.colourPosition) selection.colour = input.value;
      if (position === this.sizePosition) selection.size = input.value;
    }
    return selection;
  }

  #syncRow(row) {
    if (row.dataset.touched) return;

    const product = this.#productOf(row);
    if (!product || String(product.id) !== this.dataset.defaultProduct) return;

    const { colour, size } = this.#currentSelection();
    const colourSel = row.querySelector('[data-cb-colour]');
    const sizeSel = row.querySelector('[data-cb-size]');

    if (colour && product.colours.includes(colour) && colourSel.value !== colour) {
      colourSel.value = colour;
      // Availability is per colour, so the size list has to be rebuilt before
      // the size below can be tested against it.
      this.#fillRow(row, true);
    }

    if (size && [...sizeSel.options].some((option) => option.value === size && !option.disabled)) {
      sizeSel.value = size;
    }
  }

  #onClick = (event) => {
    const remove = event.target.closest('[data-cb-remove]');
    if (!remove) return;
    event.preventDefault();
    if (this.rows.length <= this.minPairs) return;

    remove.closest('[data-cb-row]')?.remove();
    this.#syncTierToCount();
    this.#refresh();
  };

  /** Select the bundle tier matching the current number of pairs. */
  #syncTierToCount() {
    const match = this.tiers.find((t) => Number(t.value) === this.rows.length);
    if (match && !match.checked) {
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** Re-number rows, refresh thumbnails and stock, update the benefit line. */
  #refresh() {
    this.rows.forEach((row, i) => {
      const label = row.querySelector('[data-cb-num]');
      if (label) label.textContent = `${this.dataset.pairLabel || 'Pair'} #${i + 1}`;

      const remove = row.querySelector('[data-cb-remove]');
      if (remove) remove.hidden = this.rows.length <= this.minPairs;

      const variant = this.#rowVariant(row);
      const img = row.querySelector('[data-cb-thumb]');
      if (img) {
        // Colour first: these products rarely have an image attached to the
        // individual variant, so the variant's own image is the same photo for
        // every colour and would never appear to change.
        const colour = row.querySelector('[data-cb-colour]')?.value;
        const source = this.#productOf(row)?.colourImages?.[colour] ?? variant?.image;
        if (source && img.src !== source) img.src = source;
        img.alt = variant?.title ?? '';
      }

      const stock = row.querySelector('[data-cb-stock]');
      if (stock) {
        stock.textContent = variant?.available
          ? this.dataset.inStockLabel || 'In stock'
          : this.dataset.soldOutLabel || 'Sold out';
        stock.dataset.available = String(Boolean(variant?.available));
      }
    });

    if (this.addBtn) this.addBtn.hidden = this.rows.length >= this.maxPairs;
    this.#renderBenefit();

    // A single pair has nothing to mix — its colour and size come from the
    // pickers above. The row stays in the DOM and keeps tracking them, so the
    // add to cart still reads its variant; only the panel is put away.
    this.hidden = this.rows.length < 2;
    if (this.hidden && this.disclosure) this.disclosure.open = false;

    this.#renderBar();

    if (this.summaryEl) {
      this.summaryEl.textContent = (this.dataset.summaryTemplate || 'Choose colours & sizes').replace(
        '{n}',
        String(this.rows.length)
      );
    }
  }

  /**
   * Describe the bundle in the sticky buy bar.
   *
   * The bar only appears once the customer has scrolled past the pickers, so
   * it has to answer "what am I buying" on its own. A mixed bundle names the
   * models; an unmixed one stays on colour and size, which is more useful when
   * there is nothing to disambiguate.
   */
  #renderBar() {
    const bar = this.pdp?.querySelector('[data-cpdp-bar]');
    const meta = this.pdp?.querySelector('[data-cpdp-bar-meta]');
    const title = this.pdp?.querySelector('[data-cpdp-bar-title]');
    const stack = this.pdp?.querySelector('[data-cpdp-bar-thumbs]');
    if (!meta && !stack && !title) return;

    const pairs = this.rows.map((row) => ({
      product: this.#productOf(row),
      colour: row.querySelector('[data-cb-colour]')?.value,
      size: row.querySelector('[data-cb-size]')?.value,
    }));
    if (!pairs.length) return;

    const unique = (values) => [...new Set(values.filter(Boolean))];
    const models = unique(pairs.map((pair) => pair.product?.short ?? pair.product?.title));
    const mixed = models.length > 1;

    // The heading names the shoes; the line under it describes them. Splitting
    // it that way keeps each line short and stops the two repeating each other.
    if (title) {
      this.defaultBarTitle ??= title.textContent.trim();
      title.textContent = mixed ? models.join(' + ') : this.defaultBarTitle;
    }

    bar?.setAttribute('data-mixed', String(mixed));

    if (meta) {
      // Per-pair detail in the same order and with the same separator as the
      // heading, so "Halo + Ivory" reads straight down onto "Khaki 39 + Black
      // 41". When every pair is identical there is nothing to line up, and the
      // plainer "Khaki · 39" is more useful.
      const described = pairs.map((pair) => [pair.colour, pair.size].filter(Boolean).join(' '));
      const detail =
        unique(described).length === 1
          ? [pairs[0].colour, pairs[0].size].filter(Boolean).join(' \u00b7 ')
          : described.slice(0, 3).join(' + ');

      const tier = this.tiers.find((candidate) => candidate.checked);
      meta.textContent = [detail, tier?.dataset.title].filter(Boolean).join(' \u00b7 ');
    }

    if (stack) {
      // One tile per distinct shoe-and-colour: two pairs of the same Khaki
      // Halo is one shoe, so showing it twice would just look like a mistake.
      const seen = new Set();
      const sources = [];
      for (const pair of pairs) {
        // The bar's own map first: the cut-out the cards use has no background
        // to sit on up here.
        const source = pair.product?.barImages?.[pair.colour] ?? pair.product?.colourImages?.[pair.colour];
        if (!source || seen.has(source)) continue;
        seen.add(source);
        sources.push(source);
        if (sources.length === 3) break;
      }
      if (sources.length) this.#renderThumbs(stack, sources);
    }
  }

  #renderThumbs(stack, sources) {
    const existing = [...stack.querySelectorAll('.cpdp__bar-thumb')];

    sources.forEach((source, index) => {
      let img = existing[index];
      if (!img) {
        img = document.createElement('img');
        img.className = 'cpdp__bar-thumb';
        img.width = 48;
        img.height = 48;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        if (index === 0) img.setAttribute('data-cpdp-bar-thumb', '');
        stack.append(img);
      }
      if (img.src !== source) img.src = source;
    });

    for (const extra of existing.slice(sources.length)) extra.remove();
  }

  #rowVariant(row) {
    const product = this.#productOf(row);
    const colour = row.querySelector('[data-cb-colour]')?.value;
    const size = row.querySelector('[data-cb-size]')?.value;
    return product ? this.#variant(product, colour, size) : null;
  }

  /**
   * The saving that adding one more pair would unlock, read from the tier
   * data so it can never contradict the bundle cards above.
   */
  #renderBenefit() {
    if (!this.benefitEl) return;

    const next = this.tiers
      .map((t) => ({ qty: Number(t.value), saving: t.dataset.savingText }))
      .filter((t) => t.qty > this.rows.length && t.saving)
      .sort((a, b) => a.qty - b.qty)[0];

    if (!next) {
      this.benefitEl.hidden = true;
      return;
    }

    const ordinal = this.#ordinal(next.qty);
    this.benefitEl.hidden = false;
    this.benefitEl.textContent = (this.dataset.benefitTemplate || 'Add a {n} pair and save {saving}')
      .replace('{n}', ordinal)
      .replace('{saving}', next.saving);
  }

  #ordinal(n) {
    const suffix = ['th', 'st', 'nd', 'rd'][(n % 100 - 20) % 10] || ['th', 'st', 'nd', 'rd'][n % 100] || 'th';
    return `${n}${suffix}`;
  }

  /* --------------------------------------------------------- add all --- */

  #onSubmit = async (event) => {
    if (!this.products?.length) return;
    event.preventDefault();

    const items = this.rows
      .map((row) => this.#rowVariant(row))
      .filter((variant) => variant?.available)
      .map((variant) => ({ id: variant.id, quantity: 1 }));

    if (!items.length) return;

    const buttons = [...(this.closest('conversion-pdp')?.querySelectorAll('[data-cpdp-atc], [data-cpdp-bar] button') ?? [])];
    for (const button of buttons) button.setAttribute('disabled', '');

    const sections = cartSectionIds();
    const payload = { items };
    if (sections.length) {
      payload.sections = sections.join(',');
      payload.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(await response.text());
      const result = await response.json();

      await applyCartSections(result?.sections);
      const opened = await this.#openDrawer(result?.sections);
      if (!opened) window.location.href = window.Theme?.routes?.cart_url || '/cart';
    } catch (error) {
      console.warn('[conversion-bundle] add to cart failed', error);
      window.location.href = window.Theme?.routes?.cart_url || '/cart';
    } finally {
      for (const button of buttons) button.removeAttribute('disabled');
    }
  };

  /**
   * Hands the add off to the theme's cart drawer by dispatching the same event
   * the theme's own form would. The event name comes from Shopify's standard
   * events module rather than a hard-coded string, so it cannot drift; if that
   * module or the drawer is missing, this reports failure and the caller falls
   * back to the cart page.
   *
   * @returns {Promise<boolean>} Whether the drawer took over.
   */
  async #openDrawer(sections) {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return false;

    let eventName;
    try {
      ({ cartLinesUpdate: eventName } = (await import('@shopify/events')).StandardEvents);
    } catch (error) {
      console.warn('[conversion-bundle] standard events unavailable', error);
      return false;
    }
    if (!eventName) return false;

    // `action` and `promise` are the two properties the drawer and the cart
    // line list read off this event; a plain CustomEvent detail is not where
    // they look for them.
    //
    // `sections` carries the already-rendered cart HTML so the drawer can morph
    // straight to its filled state. No `items` on purpose — /cart/add.js
    // returns only what was just added, and the listeners would read that as
    // the whole cart.
    const event = new Event(eventName, { bubbles: true });
    event.action = 'add';
    event.promise = Promise.resolve({ detail: { source: 'conversion-bundle', sections } });
    document.dispatchEvent(event);

    return true;
  }
}

if (!customElements.get('conversion-bundle')) {
  customElements.define('conversion-bundle', ConversionBundle);
}
